import { isNavigationFailure, NavigationFailureType } from 'vue-router'

import router from '@/plugins/router'
import i18n from '@/plugins/i18n'
import bus from '@/plugins/eventbus'
import store from '@/store'
import { captureHandledError, captureUntranslatedError } from '@/plugins/sentry/scope'

function openToast(opts) {
  // Ensure text is always a string to avoid [object Object] display issues
  if (opts.text && typeof opts.text === 'object') {
    console.warn('Toast text was an object in error handler, converting to string:', opts.text)
    try {
      opts.text = JSON.stringify(opts.text)
    } catch (e) {
      opts.text = 'An error occurred'
    }
  }
  bus.emit('toast:open', opts)
}

const ERRORS_TO_IGNORE = ['Invalid token specified']
function getErrorString(error) {
  // Extract message from error object and ensure it's a string
  let message = error?.message ?? error

  // If message is still an object, try to stringify it or use a default
  if (typeof message === 'object' && message !== null) {
    // Try to get error info from common error object properties
    if (message.code) {
      message = message.code
    } else if (message.type) {
      message = message.type
    } else {
      // Last resort: stringify the error object for debugging
      try {
        message = JSON.stringify(message)
        console.error('Error was an object without message property:', message, error)
      } catch (e) {
        message = 'UNHANDLED_ERROR'
        console.error('Could not stringify error object:', error)
      }
    }
  }

  // Ensure message is a string
  message = String(message)

  if (!i18n.global.tm(`errors.${message}`)) {
    if (!ERRORS_TO_IGNORE.includes(message)) {
      console.error(`Untranslated error message: ${message}`, error)
      captureUntranslatedError(message, error)
    }
    message = 'UNHANDLED_ERROR'
  }

  return i18n.global.t(`errors.${message}`)
}

function beforeHandle(error) {
  const { message, code } = error

  if (code === 'ERR_NETWORK') {
    // Only treat as offline when the device truly is offline. If the user is
    // online but a request failed (backend down, DNS, CORS, etc.) it's a real
    // signal — let it flow through to the toast and Sentry capture.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      store.commit('device/setOnline', false)
      return false
    }
  }

  switch (message) {
    case 'Invalid token specified':
    case 'SESSION_EXPIRED':
    case 'INVALID_TOKEN':
      // user needs to log in again
      store.dispatch('auth/logout', false)

      // avoid firefox specific race condition with route guard
      setTimeout(() => {
        router.push('/auth/login').catch(err => {
          if (isNavigationFailure(err, NavigationFailureType.duplicated)) return
          throw err
        })
      }, 500)

      return false
    case 'WRONG_APP_VERSION':
      store.commit('device/setForceUpdateLayout', true)
      return false

    case 'UNDER_MAINTENANCE':
      store.commit('features/setUnderMaintenance')
      return false
    default:
      return true
  }
}

// Create error handler object
const errorHandler = {
  getI18nString(error) {
    return getErrorString(error)
  },
  toast(error, opts = {}) {
    console.error(error)
    const text = this.getI18nString(error)

    const next = beforeHandle(error)
    if (!next) return

    captureHandledError(error)

    openToast({
      props: {
        color: 'red25 text-red600',
        timeout: 3e3,
      },
      text,
      ...opts,
    })
  },
}

// Setup function for Vue 3
export const setupErrorHandler = app => {
  app.config.globalProperties.$error = errorHandler
}

export default errorHandler
